import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Menu, X, ChevronDown, User, LogOut, LayoutDashboard, Shield } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'Programs', path: '/programs',
    children: [
      { label: 'Membership', path: '/programs/membership' },
      { label: 'Seat at the Table', path: '/programs/seat-at-the-table' },
      { label: 'Legal Defense Fund', path: '/programs/legal-defense-fund' },
      { label: 'Priority Zones', path: '/programs/priority-zones' },
    ]
  },
  { label: 'Store', path: '/store' },
  { label: 'News', path: '/news' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <Shield className="w-8 h-8 text-navy" />
            <div>
              <span className="font-heading font-bold text-navy text-lg leading-none">SAFE FLORIDA</span>
              <span className="font-signature text-gold text-xs block leading-none">F.C. Clark</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              link.children ? (
                <div key={link.label} className="relative"
                  onMouseEnter={() => setProgramsOpen(true)}
                  onMouseLeave={() => setProgramsOpen(false)}>
                  <Link to={link.path}
                    className={`px-3 py-2 text-sm font-body font-medium rounded-md inline-flex items-center gap-1 transition-colors ${isActive(link.path) ? 'text-navy bg-navy/5' : 'text-gray-700 hover:text-navy'}`}
                    data-testid={`nav-${link.label.toLowerCase()}`}>
                    {link.label} <ChevronDown className="w-3 h-3" />
                  </Link>
                  {programsOpen && (
                    <div className="absolute top-full left-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 animate-fade-in">
                      {link.children.map(child => (
                        <Link key={child.path} to={child.path}
                          className="block px-4 py-2 text-sm font-body text-gray-700 hover:bg-navy/5 hover:text-navy transition-colors"
                          data-testid={`nav-sub-${child.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={link.path} to={link.path}
                  className={`px-3 py-2 text-sm font-body font-medium rounded-md transition-colors ${isActive(link.path) ? 'text-navy bg-navy/5' : 'text-gray-700 hover:text-navy'}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}>
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Link to="/store" className="relative" data-testid="nav-cart-icon">
              <ShoppingCart className="w-5 h-5 text-gray-700 hover:text-navy transition-colors" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-body font-bold" data-testid="cart-badge">
                  {itemCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-body font-medium text-navy border border-navy/20 rounded-md hover:bg-navy/5 transition-colors"
                  data-testid="user-menu-button">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 animate-fade-in z-50"
                    onMouseLeave={() => setUserMenuOpen(false)}>
                    <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-navy/5 hover:text-navy" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-navy/5 hover:text-navy" data-testid="nav-admin">
                        <Shield className="w-4 h-4" /> Admin Panel
                      </Link>
                    )}
                    <button onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left" data-testid="nav-logout">
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="outline" size="sm" className="font-body text-navy border-navy/30 hover:bg-navy/5">Log In</Button>
                </Link>
                <Link to="/programs/membership" data-testid="nav-become-member">
                  <Button size="sm" className="font-body btn-primary-safe text-xs px-4">BECOME A MEMBER</Button>
                </Link>
              </div>
            )}

            {/* Mobile toggle */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white animate-fade-in" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <div key={link.label}>
                <Link to={link.path} onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-body font-medium text-gray-700 hover:bg-navy/5 rounded-md">
                  {link.label}
                </Link>
                {link.children && link.children.map(child => (
                  <Link key={child.path} to={child.path} onClick={() => setMobileOpen(false)}
                    className="block pl-8 py-1.5 text-sm font-body text-gray-500 hover:text-navy">
                    {child.label}
                  </Link>
                ))}
              </div>
            ))}
            {!user && (
              <div className="pt-2 border-t border-gray-100 space-y-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-body font-medium text-navy">Log In</Link>
                <Link to="/programs/membership" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-body font-bold text-gold">BECOME A MEMBER</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
